import { useEffect, useState } from "react";
import type { Deployment, DiffPull, DiffResult, ServiceState } from "@rebuild/shared";
import { SERVICE_LABELS, SERVICE_COLORS } from "../types";
import { PromoteDialog } from "./PromoteDialog";
import { DiffView } from "./DiffView";
import { DeploymentInfo } from "./DeploymentInfo";
import { fetchDiff } from "../api";

export function ServiceCard({ state, onPromoted }: { state: ServiceState; onPromoted: () => void }) {
  const [promoteTarget, setPromoteTarget] = useState<{
    deployment: Deployment;
    pull: DiffPull | null;
    includedPulls: DiffPull[];
  } | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [diff, setDiff] = useState<DiffResult | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const hasStaging = !!state.staging;
  const isDifferent = hasStaging && (!state.production?.commitSha || state.staging!.commitSha !== state.production.commitSha);

  const prodSha = state.production?.commitSha ?? null;
  const stagingSha = state.staging?.commitSha ?? null;
  const diffKey = isDifferent && prodSha && stagingSha ? `${prodSha}...${stagingSha}` : null;

  useEffect(() => {
    if (!diffKey || !prodSha || !stagingSha) {
      setDiff(null);
      setDiffLoading(false);
      return;
    }
    let cancelled = false;
    setDiffLoading(true);
    fetchDiff(prodSha, stagingSha)
      .then((result) => {
        if (!cancelled) setDiff(result);
      })
      .catch(() => {
        if (!cancelled) setDiff({ pulls: [], commits: 0, ahead: 0, error: "Failed to load diff" });
      })
      .finally(() => {
        if (!cancelled) setDiffLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [diffKey, prodSha, stagingSha]);

  const badgeLabel = diff && !diff.error && diff.ahead > 0
    ? `${diff.ahead} commit${diff.ahead !== 1 ? "s" : ""} ahead`
    : "Changes pending";

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <div className={`w-2.5 h-2.5 rounded-full ${SERVICE_COLORS[state.service]}`} />
          <h3 className="text-base font-semibold text-gray-900">{SERVICE_LABELS[state.service]}</h3>
          {isDifferent && (
            <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full font-medium">
              {badgeLabel}
            </span>
          )}
        </div>

        {/* Staging → Production */}
        <div className="p-4 flex items-center gap-4">
          <DeploymentInfo deployment={state.staging} label="Staging" />
          <svg className="w-5 h-5 text-gray-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <DeploymentInfo deployment={state.production} label="Production" />
        </div>

        {/* Actions */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center gap-2">
          <button
            onClick={() => state.staging && setPromoteTarget({ deployment: state.staging, pull: null, includedPulls: [] })}
            disabled={!state.staging || !isDifferent}
            className="px-3 py-1.5 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-100 disabled:text-gray-400 text-white rounded-md transition-colors"
          >
            Promote
          </button>
          {state.staging && !isDifferent && (
            <span className="text-xs text-gray-400">No changes to promote</span>
          )}
          {isDifferent && (
            <button
              onClick={() => setShowDiff((v) => !v)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                showDiff
                  ? "bg-gray-200 text-gray-700"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-600"
              }`}
            >
              {showDiff ? "Hide Changes" : "View Changes"}
            </button>
          )}
          {state.soakMinutes !== null && (
            <span className="ml-auto text-xs text-gray-400">
              On staging for{" "}
              {state.soakMinutes < 60
                ? `${state.soakMinutes}m`
                : `${Math.floor(state.soakMinutes / 60)}h ${state.soakMinutes % 60}m`}
            </span>
          )}
        </div>

        {/* Inline diff */}
        {showDiff && state.production?.commitSha && state.staging?.commitSha && (
          <div className="mx-4 my-3 p-3 rounded-md bg-gray-100">
            <DiffView
              diff={diff}
              loading={diffLoading}
              stagingHistory={state.stagingHistory ?? []}
              onDeployTarget={(pull, deployment, includedPulls) => setPromoteTarget({ deployment, pull, includedPulls })}
            />
          </div>
        )}
      </div>

      {promoteTarget && (
        <PromoteDialog
          service={state.service}
          deployment={promoteTarget.deployment}
          selectedPull={promoteTarget.pull}
          includedPulls={promoteTarget.includedPulls}
          onClose={() => setPromoteTarget(null)}
          onPromoted={() => { setPromoteTarget(null); onPromoted(); }}
        />
      )}
    </>
  );
}
